<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'contracts',
    path: '/contracts',
    component: 'Contracts',
    title: () => gettext('Contracts'),
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'Contracts',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const grid = ref(null);

  const $refs = {
    get grid() {
      return grid.value;
    },
  };

  const selectedItems = ref([]);
  const dsParams = ref({
    skip_archived: true,
  });
  const ds = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_2248226175642056_columnDefaults = computed(() => ({
    sortable: true,
    filterable: true,
    resizable: true,
  }));
  const b_2248226175642056_columns = computed(() => [
    {
      field: 'contract_number',
      name: $fn.value.pgettext('Contracts', 'Contract number'),
      type: 'command',
      formatter: {
        name: 'routeLink',
        to: 'contract',
        params: {
          id: 'id',
        },
      },
    },
    {
      field: 'contract_type',
      name: $fn.value.pgettext('Contracts', 'Type'),
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        formatters: [
          {
            name: 'gettext',
            context: 'ContractTypes',
            conversion: 'StartCase',
            filterInput: true,
          },
          {
            name: 'badge',
            variantMapSource: 'fieldValue',
            variantmap: {
              rental: 'contract-type--rental',
              vendor: 'contract-type--vendor',
              rental_agreement: 'contract-type--rental_agreement',
              service: 'contract-type--service',
              parking: 'contract-type--parking',
              unknown: 'contract-type--unknown',
            },
          },
        ],
      },
    },
    {
      field: 'status',
      name: $fn.value.pgettext('Contracts', 'Status'),
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        formatters: [
          {
            name: 'gettext',
            context: 'ContractStatus',
            conversion: 'StartCase',
            filterInput: true,
          },
          {
            name: 'badge',
            variantMapSource: 'fieldValue',
            variantmap: {
              draft: 'light',
              approved: 'success',
              signed: 'success',
              active: 'primary',
              ended: 'warning',
              terminated: 'warning',
              rescinded: 'danger',
              archived: 'light',
            },
          },
        ],
      },
    },
    {
      field: 'rental_data',
      name: $fn.value.pgettext('Contracts', 'Rental'),
      type: 'command',
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if $value }}\n\
    <strong>{{ $value.name }}</strong>\n\
    {{~#if $value.house_number }} | {{ $value.house_number }}{{/if }}\n\
    {{~#if $value.area }} ({{$value.area}}{{$value.unit_of_area}}){{/if }}\n\
  {{~/if }}\n\
</span>\n\
',
          },
          {
            name: 'routeLink',
            to: 'rental',
            navigateOnIconClick: true,
            params: {
              id: 'rental_id',
            },
          },
        ],
      },
    },
    {
      field: 'company_name',
      name: $fn.value.pgettext('Contracts', 'Lessor'),
      type: 'command',
      formatter: {
        name: 'routeLink',
        to: 'company',
        navigateOnIconClick: true,
        params: {
          id: 'company_id',
        },
      },
    },
    {
      field: 'counterparty_name',
      name: $fn.value.pgettext('Contracts', 'Tenant'),
      type: 'command',
      formatter: {
        name: 'routeLink',
        to: 'company',
        navigateOnIconClick: true,
        params: {
          id: 'counterparty_id',
        },
      },
    },
    {
      field: 'revision_number',
      name: $fn.value.pgettext('Contracts', 'Version'),
    },
    {
      field: 'revision_date',
      name: $fn.value.pgettext('Contracts', 'Revision date'),
      formatter: {
        name: 'date',
      },
    },
    {
      field: 'contract_date',
      name: $fn.value.pgettext('Contracts', 'Contract date'),
      formatter: {
        name: 'date',
      },
    },
    {
      field: 'valid_from',
      name: $fn.value.pgettext('Contracts', 'Valid From'),
      formatter: {
        name: 'date',
      },
    },
    {
      field: 'valid_to',
      name: $fn.value.pgettext('Contracts', 'Valid To'),
      formatter: {
        name: 'date',
      },
    },
    {
      field: 'next_indexation_date',
      name: $fn.value.pgettext('Contracts', 'Next indexation'),
      formatter: {
        name: 'date',
      },
    },
    {
      field: 'termination_date',
      name: $fn.value.pgettext('Contracts', 'Termination date'),
      formatter: {
        name: 'date',
      },
    },
    {
      field: 'termination_effective_date',
      name: $fn.value.pgettext('Contracts', 'Termination effective date'),
      formatter: {
        name: 'date',
      },
    },
    {
      field: 'folder_number',
      name: $fn.value.pgettext('Contracts', 'Folder number'),
    },
    {
      field: 'property_units',
      name: $fn.value.pgettext('Contracts', 'Property units'),
      toolTip: $fn.value.pgettext('Contracts', 'Property units'),
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        separator: ', ',
        formatterContext: 'item',
        formatters: [
          {
            name: 'getvalue',
            opts: {
              field: 'code',
              source: 'value',
            },
          },
        ],
      },
    },
  ]);

  const $data = computed(() => ({
    selectedItems: selectedItems.value,
    dsParams: dsParams.value,
    ds: ds.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_2248226175642056_columnDefaults: b_2248226175642056_columnDefaults.value,
    b_2248226175642056_columns: b_2248226175642056_columns.value,
  }));

  const $$load_ds = async () => {
    return $fn.value.slickDataSource('integro_api/list_contract', {
      parameters: () => dsParams.value,
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'selectedItems' },
      { name: 'dsParams', cache: { type: 'local', key: 'Contracts.dsParams' } },
      { name: 'ds' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_2248226175642056_columnDefaults', nodeId: '2248226175642056' },
      { name: 'b_2248226175642056_columns', nodeId: '2248226175642056' },
    ],
    loaders: { ds: $$load_ds },
    ux: {},
    dataRefs: {
      selectedItems,
      dsParams,
      ds,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_2248226175642056_columnDefaults,
      b_2248226175642056_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.gettext('Contracts'),
      icon: $config.value.contract.icon,
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_jrqcek9g4_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_2248226175642056_setData_fprtruq26_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value: $refs.grid.getSelectedItems(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_7172922356531116_showModal_e084nm49o_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CreateContract',
    };
  };

  const act_7315092382398562_closeModal_jrqcek9g4 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_jrqcek9g4_getActionArgs,
    });
  const inst_evh_7315092382398562_close_4q0060gy7 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_jrqcek9g4,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_4q0060gy7],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_neblkvffl =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_i0e3eo4i5 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_neblkvffl,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_i0e3eo4i5],
    event: 'reload',
  });
  const act_2248226175642056_setData_fprtruq26 = new actionTypes.SetDataAction({
    actionArgs: act_2248226175642056_setData_fprtruq26_getActionArgs,
  });
  const inst_evh_2248226175642056_selectedRowsChanged_4fje8wx2f =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_setData_fprtruq26,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_2248226175642056_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_selectedRowsChanged_4fje8wx2f],
      event: 'selectedRowsChanged',
    });
  const act_7172922356531116_showModal_e084nm49o =
    new actionTypes.ShowModalAction({
      actionArgs: act_7172922356531116_showModal_e084nm49o_getActionArgs,
    });
  const inst_evh_7172922356531116_click_4bn3e4fx7 =
    new actionTypes.EventHandlerImpl({
      action: act_7172922356531116_showModal_e084nm49o,
      event: 'click',
      displayName: 'showModal',
    });
  const inst_evh_7172922356531116_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7172922356531116_click_4bn3e4fx7],
    event: 'click',
  });

  const actions = {
    inst_evh_7315092382398562_close_4q0060gy7,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_i0e3eo4i5,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_selectedRowsChanged_4fje8wx2f,
    inst_evh_2248226175642056_selectedRowsChanged,
    inst_evh_7172922356531116_click_4bn3e4fx7,
    inst_evh_7172922356531116_click,
  };

  const evh_7315092382398562_close_4q0060gy7 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_4q0060gy7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_i0e3eo4i5 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_i0e3eo4i5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged_4fje8wx2f = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged_4fje8wx2f.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7172922356531116_click_4bn3e4fx7 = async (event, scope) => {
    return await actions.inst_evh_7172922356531116_click_4bn3e4fx7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7172922356531116_click = async (event, scope) => {
    return await actions.inst_evh_7172922356531116_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="Contracts"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <VueSlickgrid
      name="contracts"
      :headerTitle="pgettext('Contracts', 'Contracts')"
      :items="ds"
      checkbox-row-selection
      export-to-csv
      :exportToCsvFilename="pgettext('filename', 'contracts')"
      :columnDefaults="b_2248226175642056_columnDefaults"
      :columns="b_2248226175642056_columns"
      class="page--grid"
      ref="grid"
      @selectedRowsChanged="
        evh_2248226175642056_selectedRowsChanged($event, {})
      "
    >
      <template #toolbar>
        <div class="flex-grow-1 d-flex gap-2 align-items-center">
          <div>
            <ItpFormCheckbox
              name="skip_archived"
              :label="pgettext('Contracts', 'Skip archived contracts')"
              inline
              v-model="dsParams.skip_archived"
              :value="dsParams.skip_archived"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (dsParams.skip_archived =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
            >
            </ItpFormCheckbox>
          </div>
          <div class="border-start ps-3 d-flex gap-2">
            <ItpButton
              :icon="$config.contract.createContractIcon"
              :text="pgettext('Contracts', 'Create contract...')"
              size="sm"
              variant="light"
              @click="evh_7172922356531116_click($event, {})"
            >
            </ItpButton>
          </div>
          <PageHelp path="/contracts/contracts.html"> </PageHelp>
        </div>
      </template>
    </VueSlickgrid>
  </ItpPage>
</template>
